import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, ComposedChart,
  PieChart, Pie, Cell, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import ChartTooltip from "../components/ui/ChartTooltip";
import { useTranslate } from "../i18n/useTranslate";
import {
  publicComms, pilgrimAdvisories, misinformationAlerts, mediaChannels,
  pilgrimGuidance,
} from "../data/mockData";
import { googleColor } from "../data/googleColors";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { statusHex, statusClasses } from "../data/statusColors";
import {
  CheckCircle, AlertTriangle, Radio, Globe, Shield, Clock,
  ChevronDown, ChevronUp, Megaphone, Mic, Wifi, Users,
  MessageSquare, TrendingUp, Eye, Zap, Volume2, MapPin,
} from "lucide-react";

// ── inline data ──────────────────────────────────────────────────
const REACH_TREND = [
  { hour:"06:00", total:210000,  engaged:48000,  misinfo:2000  },
  { hour:"07:00", total:520000,  engaged:118000, misinfo:4200  },
  { hour:"08:00", total:1080000, engaged:242000, misinfo:8800  },
  { hour:"09:00", total:1840000, engaged:410000, misinfo:14000 },
  { hour:"10:00", total:2680000, engaged:594000, misinfo:18000 },
  { hour:"11:00", total:3200000, engaged:712000, misinfo:21000 },
  { hour:"12:00", total:3480000, engaged:784000, misinfo:19000 },
  { hour:"13:00", total:3680000, engaged:820000, misinfo:16000 },
  { hour:"14:00", total:3840000, engaged:840000, misinfo:12000 },
];

const SENTIMENT_DATA = [
  { hour:"06:00", positive:68, neutral:24, negative:8  },
  { hour:"08:00", positive:72, neutral:21, negative:7  },
  { hour:"10:00", positive:74, neutral:20, negative:6  },
  { hour:"11:00", positive:71, neutral:22, negative:7  },
  { hour:"12:00", positive:69, neutral:23, negative:8  },
  { hour:"13:00", positive:66, neutral:25, negative:9  },
  { hour:"14:00", positive:70, neutral:24, negative:6  },
];

const TRUST_COMPONENTS = [
  { label:"Verified Content Accuracy", val:94, color:chartColor(0) },
  { label:"Official Source Attribution",val:89, color:chartColor(1) },
  { label:"Advisory Response Speed",   val:82, color:chartColor(2) },
  { label:"Misinformation Correction", val:88, color:chartColor(3) },
  { label:"Multilingual Coverage",     val:91, color:chartColor(4) },
  { label:"Pilgrim Satisfaction",      val:86, color:chartColor(5) },
];

const TRUST_PIE = [
  { name:"Verified", value:87.6, color:statusHex("good") },
  { name:"Gap",      value:12.4, color:"#F1F5F9"  },
];

const ADVISORY_DETAIL = {
  "ADV-114": {
    zones:["Ramkund","Godavari Bridge","Panchvati","Camp Alpha"],
    channels:["DD Sahyadri","AIR Nashik","Official WhatsApp","PA System"],
    translation:"Marathi · Hindi · English · Gujarati",
    authorised:"DCP Nashik", aiConf:96, corrections:0,
    description:"Entry Gate 2 at Ramkund is at capacity (92%). Pilgrims are being diverted to Gate 4 via Godavari Bridge Route. Follow crowd management staff instructions.",
  },
  "ADV-113": {
    zones:["Panchvati","Camp Beta","Nashik Road","Trimbakeshwar"],
    channels:["AIR Nashik","Sarkari App","SMS Gateway","Volunteer Network"],
    translation:"Marathi · Hindi",
    authorised:"CMO Nashik", aiConf:98, corrections:0,
    description:"Free medical camps are operational at Panchvati (Camp Beta). ORS, drinking water, first-aid and heat treatment available free of cost.",
  },
  "ADV-112": {
    zones:["Nashik Road","Trimbak Road","Camp Delta"],
    channels:["Official WhatsApp","Sarkari App","SMS Gateway"],
    translation:"Hindi · Marathi · Gujarati",
    authorised:"MSRTC Nashik", aiConf:87, corrections:1,
    description:"MSRTC shuttle SR-04 towards Trimbakeshwar is delayed by 22 minutes due to traffic at Godavari Bridge. Next bus expected at Bay 7, 14:45.",
  },
  "ADV-111": {
    zones:["All Zones"],
    channels:["DD Sahyadri","AIR Nashik","SMS Gateway","PA System","Official WhatsApp","Lokmat"],
    translation:"Marathi · Hindi · English · Gujarati · Tamil · Telugu",
    authorised:"DM Nashik", aiConf:99, corrections:0,
    description:"Temperature 38°C with heat index 41°C. All pilgrims are advised to carry water, wear light-coloured clothing, use available shaded corridors and avoid crowds near Ramkund.",
  },
  "ADV-110": {
    zones:["Panchvati","Sector 7"],
    channels:["PA System","Official WhatsApp","DD Sahyadri"],
    translation:"Marathi · Hindi",
    authorised:"UNIT-P14", aiConf:90, corrections:0,
    description:"A child (boy, approximately 7 years, yellow kurta) was last seen in Panchvati Sector 7 at 14:12. Parents or anyone with information contact Helpline 14425 or nearest Police Post.",
  },
};

const MISINFO_DETAIL = {
  "MIS-28": {
    falseContent:"Ramkund is closed for 3 hours due to stampede",
    truthFact:"Ramkund is OPEN. Crowd diversion activated at Gate 2 only. Gate 4 operating normally.",
    correctionBroadcast:"Official WhatsApp · AIR Nashik · SMS Gateway — 14:14",
    correctionReach:38000, responseTime:"4 min", aiDetection:94,
    reportSource:"WhatsApp monitoring cell", harmScore:"HIGH",
  },
  "MIS-27": {
    falseContent:"Stampede at Panchvati — many injured",
    truthFact:"No stampede at Panchvati. Crowd density normal. Minor incident at Ramkund handled.",
    correctionBroadcast:"Pending — escalated to SM cell",
    correctionReach:0, responseTime:"Pending", aiDetection:88,
    reportSource:"Twitter/X trending monitor", harmScore:"HIGH",
  },
  "MIS-26": {
    falseContent:"Route via Godavari Bridge closed for private vehicles",
    truthFact:"Godavari Bridge is open for authorised pilgrim buses and shuttle only. Verified advisory ADV-114 issued.",
    correctionBroadcast:"Official WhatsApp · DD Sahyadri — 13:28",
    correctionReach:22000, responseTime:"8 min", aiDetection:91,
    reportSource:"Facebook monitoring cell", harmScore:"MED",
  },
  "MIS-25": {
    falseContent:"Medical camp at Kalaram closed — go to Panchvati",
    truthFact:"Kalaram medical post is operational 24×7. Panchvati Camp Beta also operational.",
    correctionBroadcast:"SMS Gateway · Volunteer Network — 12:52",
    correctionReach:10000, responseTime:"8 min", aiDetection:89,
    reportSource:"SMS chain interception", harmScore:"MED",
  },
};

const ZONE_ADVISORY = [
  { zone:"Ramkund Ghat",     advisories:14, channels:6, languages:4, lastBroadcast:"14:20", reach:"1.2L", status:"Active"   },
  { zone:"Panchvati",        advisories:11, channels:5, languages:4, lastBroadcast:"14:10", reach:"0.9L", status:"Active"   },
  { zone:"Trimbakeshwar",    advisories:9,  channels:4, languages:3, lastBroadcast:"13:58", reach:"0.7L", status:"Active"   },
  { zone:"Nashik Road",      advisories:8,  channels:5, languages:4, lastBroadcast:"14:15", reach:"0.6L", status:"Active"   },
  { zone:"Gangapur Camp",    advisories:6,  channels:4, languages:3, lastBroadcast:"13:44", reach:"0.4L", status:"Active"   },
  { zone:"Kalaram Temple",   advisories:7,  channels:4, languages:3, lastBroadcast:"14:05", reach:"0.5L", status:"Active"   },
];

const CHANNEL_ANALYTICS = [
  { channel:"DD Sahyadri",      type:"TV",      sent:22, delivered:22, failed:0, openRate:92, reach:"12.4M", lastBroadcast:"14:30", nextBroadcast:"15:00", signal:"Excellent" },
  { channel:"AIR Nashik",       type:"Radio",   sent:38, delivered:38, failed:0, openRate:88, reach:"4.2M",  lastBroadcast:"14:25", nextBroadcast:"14:45", signal:"Excellent" },
  { channel:"Official WhatsApp",type:"Digital", sent:56, delivered:54, failed:2, openRate:71, reach:"1.8M",  lastBroadcast:"14:32", nextBroadcast:"14:50", signal:"Good"      },
  { channel:"SMS Gateway",      type:"SMS",     sent:184,delivered:178,failed:6, openRate:64, reach:"1.4M",  lastBroadcast:"14:28", nextBroadcast:"14:40", signal:"Good"      },
  { channel:"Sarkari App",      type:"App",     sent:42, delivered:41, failed:1, openRate:82, reach:"0.6M",  lastBroadcast:"14:28", nextBroadcast:"15:00", signal:"Good"      },
  { channel:"PA System",        type:"PA",      sent:96, delivered:96, failed:0, openRate:100,reach:"0.3M",  lastBroadcast:"14:35", nextBroadcast:"14:45", signal:"Live"      },
  { channel:"Lokmat Nashik",    type:"Print",   sent:2,  delivered:2,  failed:0, openRate:100,reach:"2.8M",  lastBroadcast:"13:00", nextBroadcast:"18:00", signal:"Good"      },
  { channel:"Volunteer Network",type:"Network", sent:28, delivered:27, failed:1, openRate:96, reach:"0.2M",  lastBroadcast:"14:30", nextBroadcast:"15:00", signal:"Good"      },
];

const LANGUAGE_REACH = [
  { lang:"Marathi",  advisories:18, reach:2100000, pct:42 },
  { lang:"Hindi",    advisories:14, reach:1600000, pct:32 },
  { lang:"English",  advisories:8,  reach:600000,  pct:12 },
  { lang:"Gujarati", advisories:5,  reach:450000,  pct:9  },
  { lang:"Telugu",   advisories:2,  reach:80000,   pct:3  },
  { lang:"Tamil",    advisories:1,  reach:40000,   pct:2  },
];

const SOCIAL_MEDIA = [
  { topic:"#Simhastha2027",            platform:"Twitter/X",  volume:"48.2K",  sentiment:"Positive", status:"Monitoring",  govt:"Engaged"   },
  { topic:"Ramkund crowd",             platform:"WhatsApp",   volume:"12.4K",  sentiment:"Mixed",    status:"Active",      govt:"Correcting"},
  { topic:"#KumbhSafety",              platform:"Twitter/X",  volume:"8.1K",   sentiment:"Positive", status:"Promoted",    govt:"Boosted"   },
  { topic:"Stampede false rumour",     platform:"Twitter/X",  volume:"6.8K",   sentiment:"Negative", status:"Flagged",     govt:"Counter"   },
  { topic:"NashikKumbh bus info",      platform:"Facebook",   volume:"5.4K",   sentiment:"Neutral",  status:"Monitoring",  govt:"Responded" },
  { topic:"Medical camp locations",    platform:"Facebook",   volume:"4.2K",   sentiment:"Positive", status:"Promoted",    govt:"Boosted"   },
  { topic:"ORS water distribution",    platform:"Instagram",  volume:"3.8K",   sentiment:"Positive", status:"Promoted",    govt:"Engaged"   },
  { topic:"Route closed false info",   platform:"Facebook",   volume:"3.2K",   sentiment:"Negative", status:"Flagged",     govt:"Counter"   },
];

const PRESS_BRIEFINGS = [
  { time:"15:00", title:"District Collector Press Conference — Live Situation Update",    authority:"DM Nashik",      venue:"Nashik Collectorate", status:"Upcoming" },
  { time:"13:00", title:"DCP Nashik Media Briefing — Crowd Management Measures",        authority:"DCP Nashik",     venue:"Police HQ",           status:"Completed"},
  { time:"11:00", title:"CMO Daily Health Bulletin — Camp Status & Heat Advisory",      authority:"CMO Nashik",     venue:"Civil Hospital",      status:"Completed"},
  { time:"16:30", title:"CM Office Media Interaction — Simhastha Preparedness",         authority:"CM Office",      venue:"Nashik Circuit House",status:"Scheduled"},
  { time:"18:00", title:"Maha Kumbh AI Evening Situation Report — War Room Broadcast",          authority:"Nodal Officer",  venue:"Maha Kumbh AI War Room",      status:"Scheduled"},
];

const COMMUNITY_LEADERS = [
  { name:"Pujari Mahant Ramdas Swami",  area:"Ramkund Ghat",        role:"Head Pujari",      contactTime:"13:45", status:"Briefed",   msg:"Crowd advisory shared with temple committee" },
  { name:"Sarpanch Shri Govind Patil",  area:"Panchvati Ward",      role:"Ward Sarpanch",    contactTime:"12:30", status:"Briefed",   msg:"Volunteer coordination confirmed for 500 people" },
  { name:"UNIT-H08",         area:"Nashik Road Colony",  role:"RWA President",    contactTime:"11:00", status:"Briefed",   msg:"Water distribution points shared with residents" },
  { name:"Imam Sahab Qazi Siddiqui",    area:"Gangapur Area",       role:"Community Leader", contactTime:"10:15", status:"Briefed",   msg:"Prayer timing coordination to avoid peak crowd" },
  { name:"Smt. Lata Borade",            area:"Trimbakeshwar Village",role:"Grampanchayat",   contactTime:"09:30", status:"Briefed",   msg:"Alternate route map distributed in village" },
  { name:"Rev. Fr. Thomas Mendez",      area:"Nashik City",         role:"Church Leader",    contactTime:"09:00", status:"Briefed",   msg:"Church network spreading safety advisories" },
];

const BROADCAST_HISTORY = [
  { time:"14:32", title:"Gate 2 Diversion — Marathi/Hindi", channels:4, reach:"64K",  type:"Route",     lang:"Marathi/Hindi" },
  { time:"14:20", title:"Heat Advisory — All Languages",    channels:6, reach:"1.2L", type:"Safety",    lang:"6 Languages"   },
  { time:"14:12", title:"Missing Child Alert — Panchvati",  channels:3, reach:"22K",  type:"Missing",   lang:"Marathi/Hindi" },
  { time:"14:10", title:"Medical Camp Open — Camp Beta",    channels:4, reach:"44K",  type:"Medical",   lang:"Marathi"       },
  { time:"13:58", title:"Bus SR-04 Delay Update",           channels:3, reach:"18K",  type:"Transport", lang:"Hindi/Marathi" },
  { time:"13:44", title:"MIS-28 Correction — Ramkund Open",channels:4, reach:"38K",  type:"Correction",lang:"Marathi/Hindi" },
  { time:"13:30", title:"Afternoon Crowd Advisory",         channels:5, reach:"82K",  type:"Safety",    lang:"All"           },
  { time:"12:15", title:"ORS Distribution — Zone C Added",  channels:3, reach:"24K",  type:"Medical",   lang:"Marathi/Hindi" },
];

const HISTORICAL = [
  { metric:"Total Reach",       k2015:"1.8M",  k2019:"3.1M",  k2027:"38.4M", delta:"+1140%" },
  { metric:"Advisories Issued", k2015:"18",    k2019:"28",    k2027:"42",    delta:"+133%"  },
  { metric:"Languages",         k2015:"2",     k2019:"3",     k2027:"6",     delta:"+200%"  },
  { metric:"Misinfo Corrected", k2015:"N/A",   k2019:"4",     k2027:"7/9",   delta:"First"  },
  { metric:"Response Time",     k2015:"45 min",k2019:"18 min",k2027:"4 min", delta:"-91%"   },
  { metric:"Digital Channels",  k2015:"1",     k2019:"3",     k2027:"6",     delta:"+500%"  },
  { metric:"Trust Score",       k2015:"N/A",   k2019:"71%",   k2027:"87.6%", delta:"+16 pt" },
];

const BROADCAST_TYPE_COLORS = {
  Route: chartColor(0), Safety: chartColor(1), Medical: chartColor(2),
  Transport: chartColor(3), Missing: chartColor(4), Correction: chartColor(5),
};

// ── helpers ──────────────────────────────────────────────────────
const typeBadge = t =>
  t === "Safety"     ? "badge badge-critical" :
  t === "Medical"    ? "badge badge-high"     :
  t === "Route"      ? "badge badge-info"     :
  t === "Transport"  ? "badge badge-warning"  :
  t === "Missing"    ? "badge badge-purple"   :
  t === "Correction" ? "badge badge-navy"     : "badge badge-info";

const chTypeBg = t =>
  t === "TV"      ? "gov-gradient text-white text-[9px] font-black px-2 py-0.5 rounded-full"           :
  t === "Radio"   ? "bg-purple-100 text-purple-700 text-[9px] font-black px-2 py-0.5 rounded-full"     :
  t === "Digital" ? "bg-emerald-100 text-emerald-700 text-[9px] font-black px-2 py-0.5 rounded-full"   :
  t === "App"     ? "bg-amber-100 text-amber-700 text-[9px] font-black px-2 py-0.5 rounded-full"       :
  t === "SMS"     ? "bg-blue-100 text-blue-700 text-[9px] font-black px-2 py-0.5 rounded-full"         :
  t === "PA"      ? "bg-red-100 text-red-700 text-[9px] font-black px-2 py-0.5 rounded-full"           :
  t === "Network" ? "bg-teal-100 text-teal-700 text-[9px] font-black px-2 py-0.5 rounded-full"         :
  "bg-slate-100 text-slate-700 text-[9px] font-black px-2 py-0.5 rounded-full";

const SENTIMENT_BUCKET = { Positive:"good", Negative:"critical", Mixed:"warning" };
const sentBg = s => statusClasses(SENTIMENT_BUCKET[s] || "neutral");

const GOVT_ACTION_BUCKET = { Counter:"critical", Correcting:"serious", Boosted:"good", Engaged:"info" };
const govBg = s => statusClasses(GOVT_ACTION_BUCKET[s] || "neutral");

const PRESS_STATUS_BUCKET = { Upcoming:"warning", Completed:"good" };
const pressBg = s => statusClasses(PRESS_STATUS_BUCKET[s] || "info");

// ── component ────────────────────────────────────────────────────
export default function PublicComms() {
  const t = useTranslate();
  const [expandedAdv,   setExpandedAdv]   = useState(null);
  const [expandedMis,   setExpandedMis]   = useState(null);
  const [channelTab,    setChannelTab]    = useState("overview");
  const [broadcastType, setBroadcastType] = useState("all");

  const filteredHistory = broadcastType === "all"
    ? BROADCAST_HISTORY
    : BROADCAST_HISTORY.filter(b => b.type === broadcastType);

  return (
    <div className="space-y-8">
      <SectionHeader icon="Megaphone" title="Public Communication & Trust Layer"
        subtitle="360° verified advisory system · misinformation command centre · multilingual broadcast · social media war room · community leader network · press briefings · channel analytics · trust scoring · Simhastha Kumbh 2027"
        badge="87.6% TRUST"
      />

      {/* KPIs — computed from publicComms / the arrays rendered below,
          instead of separately typed numbers that can drift from them. */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <MetricCard title="Verified Advisories" value={publicComms.verifiedAlerts.toString()} icon="CheckCircle" color="green"  index={0} />
        <MetricCard title="Total Reach"          value={`${(publicComms.messageReach / 100000).toFixed(1)}L`} icon="Globe" color="blue" index={1} live />
        <MetricCard title="Trust Score"          value={`${publicComms.trustScore}%`} icon="Shield"        color="green"  index={2} />
        <MetricCard title="Misinfo Reports"      value={publicComms.misinfoReports.toString()} icon="AlertTriangle" color="red" index={3} live />
        <MetricCard title="Broadcasts Sent"      value={CHANNEL_ANALYTICS.reduce((s, c) => s + c.sent, 0).toString()} icon="Megaphone" color="blue" index={4} />
        <MetricCard title="Languages Covered"    value={LANGUAGE_REACH.length.toString()} icon="Globe"         color="purple" index={5} />
        <MetricCard title="Corrected Today"      value="7 / 9"  icon="CheckCircle"   color="green"  index={6} />
        <MetricCard title="AI Fact-Checks"       value="124"    icon="Brain"         color="purple" index={7} />
      </div>

      {/* Command Banner */}
      <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl bg-white border border-slate-200 shadow-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-blue-400 live-dot" />
              <span className="text-[10px] font-black text-saffron-dark tracking-widest">
                {t("Maha Kumbh AI PUBLIC COMMS COMMAND — NODAL OFFICER NASHIK — 14:35 IST — ALL CHANNELS LIVE")}
              </span>
            </div>
            <h3 className="text-base font-black text-slate-900">
              {t("Public Communication & Trust Layer — Simhastha Nashik 2027")}
            </h3>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              {t("42 verified advisories · 38.4 lakh reach · 6 languages · 8 broadcast channels · 9 misinfo reports (7 corrected) · Trust score 87.6% · 4-min avg response")}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {[
              { ch:"DD TV",    stat:"Live" },
              { ch:"AIR FM",   stat:"Live" },
              { ch:"WhatsApp", stat:"Live" },
              { ch:"SMS",      stat:"Live" },
              { ch:"PA",       stat:"Live" },
            ].map((c, i) => {
              const gc = googleColor(i);
              return (
              <div key={c.ch}
                className="rounded-xl px-2.5 py-2 text-center min-w-[64px] shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                style={{ background:gc.bgLight, border:`1px solid ${gc.border}` }}>
                <div className="flex items-center justify-center gap-1 mb-0.5">
                  <div className="w-1.5 h-1.5 rounded-full live-dot" style={{ background:gc.text }} />
                  <span className="text-[8px] font-black uppercase tracking-wide" style={{ color:gc.text }}>{c.ch}</span>
                </div>
                <span className="text-[9px] font-black" style={{ color:gc.text }}>{t(c.stat)}</span>
              </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Trust Score + Reach Trend */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <ChartCard title="Public Trust Score" subtitle="Real-time government credibility index">
          <div className="flex flex-col items-center">
            <div className="relative w-40 h-40">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={TRUST_PIE} dataKey="value" cx="50%" cy="50%"
                    innerRadius={54} outerRadius={70} startAngle={90} endAngle={-270}>
                    {TRUST_PIE.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-black text-emerald-600">87.6%</span>
                <span className="text-[10px] text-slate-500">{t("Trust Index")}</span>
              </div>
            </div>
            <div className="w-full space-y-2 mt-3">
              {TRUST_COMPONENTS.map(m => (
                <div key={m.label}>
                  <div className="flex justify-between text-[9px] mb-0.5">
                    <span className="text-slate-600 font-semibold">{t(m.label)}</span>
                    <span className="font-black" style={{ color:m.color }}>{m.val}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <motion.div className="h-1.5 rounded-full" style={{ background:m.color }}
                      initial={{ width:0 }} animate={{ width:`${m.val}%` }} transition={{ duration:0.8 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>

        <div className="md:col-span-2">
          <ChartCard title="Reach, Engagement & Misinformation Trend"
            subtitle="Cumulative advisory reach vs engagement vs misinfo reach — hourly" live>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={REACH_TREND} margin={{ top:5, right:10, left:-10, bottom:0 }}>
                <defs>
                  <linearGradient id="reachGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={chartColor(0)} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={chartColor(0)} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="engGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={chartColor(2)} stopOpacity={0.2} />
                    <stop offset="100%" stopColor={chartColor(2)} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="hour" tick={axisTick} />
                <YAxis tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : `${(v/1000).toFixed(0)}K`} tick={axisTick} />
                <Tooltip content={<ChartTooltip formatter={(v, n) => [v?.toLocaleString(), n]} />} />
                <Legend wrapperStyle={legendStyle} />
                <Area type="monotone" dataKey="total"    fill="url(#reachGrad)" stroke={chartColor(0)} strokeWidth={2.5} name={t("Total Reach")} />
                <Area type="monotone" dataKey="engaged"  fill="url(#engGrad)"   stroke={chartColor(2)} strokeWidth={2}   name={t("Engaged")} />
                <Line type="monotone" dataKey="misinfo"  stroke={statusHex("critical")} strokeWidth={2} strokeDasharray="4 2" dot={{ r:3 }} name={t("Misinfo Reach")} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>

      {/* Public Sentiment Tracker */}
      <ChartCard title="Public Sentiment Tracker — Real-time"
        subtitle="AI-monitored public sentiment across digital and helpline channels — positive / neutral / negative" live>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={SENTIMENT_DATA} margin={{ top:5, right:10, left:-10, bottom:0 }}>
            <defs>
              <linearGradient id="posGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={statusHex("good")} stopOpacity={0.3} />
                <stop offset="100%" stopColor={statusHex("good")} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="neutGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={statusHex("neutral")} stopOpacity={0.2} />
                <stop offset="100%" stopColor={statusHex("neutral")} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="negGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={statusHex("critical")} stopOpacity={0.3} />
                <stop offset="100%" stopColor={statusHex("critical")} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="hour" tick={axisTick} />
            <YAxis unit="%" tick={axisTick} />
            <Tooltip content={<ChartTooltip formatter={(v, n) => [`${v}%`, n]} />} />
            <Legend wrapperStyle={legendStyle} />
            <Area type="monotone" dataKey="positive" fill="url(#posGrad)"  stroke={statusHex("good")} strokeWidth={2.5} name={t("Positive")} />
            <Area type="monotone" dataKey="neutral"  fill="url(#neutGrad)" stroke={statusHex("neutral")} strokeWidth={1.5} name={t("Neutral")}  />
            <Area type="monotone" dataKey="negative" fill="url(#negGrad)"  stroke={statusHex("critical")} strokeWidth={2}   name={t("Negative")} />
          </AreaChart>
        </ResponsiveContainer>
        <p className="text-[9px] text-violet-600 font-bold mt-2">
          ⚠ {t("AI sentiment analysis — subject to human editorial review. DPDP 2023 compliant. No individual profiling.")}
        </p>
      </ChartCard>

      {/* Verified Advisories — expandable */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-1">
          <h3 className="text-sm font-black text-slate-900">{t("Verified Public Advisories — Active")}</h3>
          <span className="text-[10px] text-slate-400">
            {t("Showing")} {pilgrimAdvisories.length} {t("of")} {publicComms.verifiedAlerts} {t("issued today")}
          </span>
        </div>
        <div className="space-y-2">
          {pilgrimAdvisories.map((adv, i) => {
            const detail = ADVISORY_DETAIL[adv.id];
            const isOpen = expandedAdv === adv.id;
            return (
              <motion.div key={adv.id}
                initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.07 }}
                className={`rounded-2xl border cursor-pointer transition-all ${
                  isOpen ? "border-blue-300 shadow-card-hover" : "border-slate-200 hover:border-blue-200"
                }`}
                onClick={() => setExpandedAdv(isOpen ? null : adv.id)}
              >
                <div className="p-4 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className="text-[9px] font-black text-slate-400">{adv.id}</span>
                      <span className={typeBadge(adv.type)}>{t(adv.type)}</span>
                      <span className="badge badge-navy text-[9px]">{t(adv.lang)}</span>
                      {adv.verified && (
                        <span className="badge badge-success text-[9px]">
                          <CheckCircle size={9} className="mr-0.5" />{t("Verified")}
                        </span>
                      )}
                      {detail && <span className="text-[9px] text-violet-600 font-bold">{t("AI conf:")} {detail.aiConf}%</span>}
                    </div>
                    <p className="text-xs font-semibold text-slate-800 leading-snug">{t(adv.message)}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-black text-blue-700">{(adv.reach/1000).toFixed(0)}K</div>
                    <div className="text-[9px] text-slate-500">{t("reach")}</div>
                    <div className="text-[9px] text-slate-400 mt-0.5 flex items-center gap-1 justify-end">
                      <Clock size={9} />{adv.time}
                    </div>
                  </div>
                  {isOpen ? <ChevronUp size={14} className="text-slate-400 flex-shrink-0 mt-1" /> :
                             <ChevronDown size={14} className="text-slate-400 flex-shrink-0 mt-1" />}
                </div>
                <AnimatePresence>
                  {isOpen && detail && (
                    <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
                      exit={{ height:0, opacity:0 }} transition={{ duration:0.22 }}
                      className="overflow-hidden border-t border-slate-200">
                      <div className="p-4 bg-blue-50/40 rounded-b-2xl grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <div>
                            <div className="text-[9px] font-black text-slate-500 uppercase mb-1">{t("Full Message")}</div>
                            <p className="text-xs text-slate-700 leading-relaxed">{t(detail.description)}</p>
                          </div>
                          <div>
                            <div className="text-[9px] font-black text-slate-500 uppercase mb-1">{t("Authorised By")}</div>
                            <p className="text-xs font-bold text-blue-700">{detail.authorised}</p>
                          </div>
                          <div>
                            <div className="text-[9px] font-black text-slate-500 uppercase mb-1">{t("Translations")}</div>
                            <p className="text-xs text-slate-700">{t(detail.translation)}</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div>
                            <div className="text-[9px] font-black text-slate-500 uppercase mb-1">{t("Broadcast Channels")}</div>
                            <div className="flex flex-wrap gap-1">
                              {detail.channels.map(ch => (
                                <span key={ch} className="text-[8px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">{t(ch)}</span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <div className="text-[9px] font-black text-slate-500 uppercase mb-1">{t("Target Zones")}</div>
                            <div className="flex flex-wrap gap-1">
                              {detail.zones.map(z => (
                                <span key={z} className="text-[8px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full font-bold">{t(z)}</span>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 pt-1">
                            <span className={`text-[8px] font-black px-2 py-0.5 rounded-full ${detail.corrections === 0 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                              {detail.corrections === 0 ? `✓ ${t("No corrections needed")}` : `${detail.corrections} ${t("correction issued")}`}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Misinformation Command Centre */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="text-sm font-black text-slate-900">{t("Misinformation Command Centre")}</h3>
            <p className="text-[10px] text-slate-500">
              {t("AI-detected false content · platform monitoring · government correction deployed")}
              {" · "}{t("Showing")} {misinformationAlerts.length} {t("of")} {publicComms.misinfoReports} {t("reports today")}
            </p>
          </div>
          <span className="badge badge-live text-[9px]"><span className="w-1.5 h-1.5 rounded-full bg-red-500 live-dot mr-1" />{t("Live Monitor")}</span>
        </div>
        <div className="space-y-2">
          {misinformationAlerts.map((m, i) => {
            const detail = MISINFO_DETAIL[m.id];
            const isOpen = expandedMis === m.id;
            return (
              <motion.div key={m.id}
                initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                className={`rounded-2xl border cursor-pointer transition-all ${
                  m.severity === "HIGH"
                    ? isOpen ? "border-red-400 shadow-card-hover bg-red-50/20"    : "border-red-200 bg-red-50/10"
                    : isOpen ? "border-amber-400 shadow-card-hover bg-amber-50/20" : "border-amber-200 bg-amber-50/10"
                }`}
                onClick={() => setExpandedMis(isOpen ? null : m.id)}
              >
                <div className="p-4 flex items-start gap-3">
                  <AlertTriangle size={16} className={m.severity === "HIGH" ? "text-red-500 flex-shrink-0 mt-0.5" : "text-amber-500 flex-shrink-0 mt-0.5"} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                      <span className="font-mono text-[9px] text-slate-400">{m.id}</span>
                      <span className="badge badge-navy text-[9px]">{t(m.platform)}</span>
                      <span className={`badge text-[9px] ${m.severity === "HIGH" ? "badge-critical" : "badge-warning"}`}>{t(m.severity)}</span>
                      {detail && <span className="text-[9px] text-violet-600 font-bold">{t("AI detect:")} {detail.aiDetection}%</span>}
                    </div>
                    <p className="text-xs font-semibold text-slate-800">"{t(m.content)}"</p>
                    <div className="flex items-center gap-3 mt-1 text-[9px] text-slate-500 flex-wrap">
                      <span>{t("Misinfo reach:")} <strong className="text-red-600">{(m.reach/1000).toFixed(0)}K</strong></span>
                      <span>{m.time}</span>
                      {detail && <span className="text-[9px] font-bold text-amber-700">{t("Harm:")} {t(detail.harmScore)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`badge text-[9px] ${m.status === "Corrected" ? "badge-success" : "badge-warning"}`}>{t(m.status)}</span>
                    {isOpen ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                  </div>
                </div>
                <AnimatePresence>
                  {isOpen && detail && (
                    <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
                      exit={{ height:0, opacity:0 }} transition={{ duration:0.22 }}
                      className="overflow-hidden border-t border-red-100">
                      <div className="p-4 bg-white/60 rounded-b-2xl grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <div>
                            <div className="text-[9px] font-black text-red-600 uppercase mb-1">{t("False Content")}</div>
                            <p className="text-xs text-red-700 font-semibold bg-red-50 p-2 rounded-lg border border-red-100">"{t(detail.falseContent)}"</p>
                          </div>
                          <div>
                            <div className="text-[9px] font-black text-emerald-600 uppercase mb-1">{t("Government Fact")}</div>
                            <p className="text-xs text-emerald-700 font-semibold bg-emerald-50 p-2 rounded-lg border border-emerald-100">{t(detail.truthFact)}</p>
                          </div>
                          <div className="text-[9px] text-slate-500">{t("Reported by:")} {t(detail.reportSource)}</div>
                        </div>
                        <div className="space-y-2">
                          <div>
                            <div className="text-[9px] font-black text-slate-500 uppercase mb-1">{t("Correction Broadcast")}</div>
                            <p className="text-[10px] text-slate-700">{t(detail.correctionBroadcast)}</p>
                          </div>
                          {detail.correctionReach > 0 && (
                            <div>
                              <div className="text-[9px] font-black text-slate-500 uppercase mb-1">{t("Correction Reach")}</div>
                              <p className="text-sm font-black text-blue-700">{(detail.correctionReach/1000).toFixed(0)}K {t("pilgrims")}</p>
                            </div>
                          )}
                          <div>
                            <div className="text-[9px] font-black text-slate-500 uppercase mb-1">{t("Response Time")}</div>
                            <p className="text-xs font-bold text-emerald-700">{t(detail.responseTime)}</p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Media Channels */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="text-sm font-black text-slate-900">{t("Broadcast Channel Dashboard")}</h3>
            <p className="text-[10px] text-slate-500">{t("8 live channels · delivery rate · reach · next scheduled broadcast")}</p>
          </div>
          <div className="flex gap-1">
            {["overview","analytics"].map(tabKey => (
              <button key={tabKey} onClick={() => setChannelTab(tabKey)}
                className={`text-[9px] font-black px-3 py-1.5 rounded-lg transition-colors capitalize ${
                  channelTab === tabKey ? "gov-gradient text-white" : "bg-slate-100 text-slate-600"
                }`}>{t(tabKey)}</button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {channelTab === "overview" && (
            <motion.div key="ov" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              className="space-y-2">
              {CHANNEL_ANALYTICS.map((ch, i) => (
                <motion.div key={ch.channel}
                  initial={{ opacity:0, y:3 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.06 }}
                  className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors flex-wrap"
                >
                  <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    {ch.type === "TV" ? <Eye size={14} className="text-blue-600" /> :
                     ch.type === "Radio" ? <Radio size={14} className="text-purple-600" /> :
                     ch.type === "PA" ? <Volume2 size={14} className="text-red-600" /> :
                     <Wifi size={14} className="text-emerald-600" />}
                  </div>
                  <div className="flex-1 min-w-[160px]">
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                      <span className="text-xs font-black text-slate-900">{t(ch.channel)}</span>
                      <span className={chTypeBg(ch.type)}>{t(ch.type)}</span>
                    </div>
                    <div className="text-[9px] text-slate-500">
                      {t("Next:")} <strong className="text-blue-700">{ch.nextBroadcast}</strong>
                      {" · "}{t("Signal:")} <strong className="text-emerald-600">{t(ch.signal)}</strong>
                    </div>
                  </div>
                  <div className="text-center flex-shrink-0 min-w-[50px]">
                    <div className="text-sm font-black text-blue-700">{ch.reach}</div>
                    <div className="text-[9px] text-slate-500">{t("reach")}</div>
                  </div>
                  <div className="text-center flex-shrink-0 min-w-[50px]">
                    <div className="text-sm font-black text-emerald-600">{ch.openRate}%</div>
                    <div className="text-[9px] text-slate-500">{t("open rate")}</div>
                  </div>
                  <div className="text-center flex-shrink-0 min-w-[60px]">
                    <div className="text-xs font-black text-slate-700">{ch.delivered}/{ch.sent}</div>
                    <div className="text-[9px] text-slate-500">{t("delivered")}</div>
                    {ch.failed > 0 && <div className="text-[9px] text-red-600 font-bold">{ch.failed} {t("failed")}</div>}
                  </div>
                  <span className="badge badge-live text-[9px] flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot mr-1" />{t("Active")}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          )}

          {channelTab === "analytics" && (
            <motion.div key="an" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={CHANNEL_ANALYTICS.map(c => ({ name:c.channel.split(" ")[0], sent:c.sent, delivered:c.delivered, failed:c.failed }))}
                  margin={{ top:5, right:10, left:-10, bottom:0 }}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="name" tick={axisTick} />
                  <YAxis tick={axisTick} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={legendStyle} />
                  <Bar dataKey="sent"      fill={chartColor(0)} radius={[3,3,0,0]} name={t("Sent")} />
                  <Bar dataKey="delivered" fill={statusHex("good")} radius={[3,3,0,0]} name={t("Delivered")} />
                  <Bar dataKey="failed"    fill={statusHex("critical")} radius={[3,3,0,0]} name={t("Failed")} />
                </BarChart>
              </ResponsiveContainer>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Multilingual Coverage + Zone Advisory Delivery */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Multilingual Advisory Coverage"
          subtitle="Advisory count and total reach per language — Simhastha 2027">
          <div className="space-y-2 mb-3">
            {LANGUAGE_REACH.map((l, i) => (
              <motion.div key={l.lang}
                initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                className="flex items-center gap-3">
                <div className="w-16 text-[9px] font-black text-slate-700 flex-shrink-0">{t(l.lang)}</div>
                <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden relative">
                  <motion.div className="h-5 rounded-full flex items-center justify-end pr-2"
                    style={{ background: pilgrimGuidance.languages.find(x => x.name === l.lang)?.color || "#64748B" }}
                    initial={{ width:0 }} animate={{ width:`${l.pct}%` }} transition={{ duration:0.8 }}>
                    <span className="text-[8px] font-black text-white">{l.pct}%</span>
                  </motion.div>
                </div>
                <div className="text-right flex-shrink-0 w-20">
                  <div className="text-[10px] font-black text-blue-700">{(l.reach/100000).toFixed(1)}L</div>
                  <div className="text-[8px] text-slate-400">{l.advisories} {t("advisories")}</div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="pt-3 border-t border-slate-100 text-center">
            <span className="text-[9px] text-violet-600 font-bold">
              {t("AI translation covers 6 languages · Auto-approved for standard advisories · Human review for critical alerts")}
            </span>
          </div>
        </ChartCard>

        <ChartCard title="Zone-wise Advisory Delivery Status"
          subtitle="Active zones with advisory count, channel coverage and last broadcast">
          <div className="space-y-2">
            {ZONE_ADVISORY.map((z, i) => (
              <motion.div key={z.zone}
                initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                className="p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[120px]">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <MapPin size={10} className="text-blue-600" />
                      <Link to="/crowd-intelligence" className="text-xs font-black text-slate-900 hover:text-blue-700 hover:underline">{t(z.zone)}</Link>
                    </div>
                    <div className="text-[9px] text-slate-500">
                      {t("Last:")} <strong>{z.lastBroadcast}</strong>
                      {" · "}{z.channels} {t("channels")} · {z.languages} {t("languages")}
                    </div>
                  </div>
                  <div className="flex gap-3 flex-shrink-0 text-center">
                    <div>
                      <div className="text-sm font-black text-blue-700">{z.advisories}</div>
                      <div className="text-[8px] text-slate-400">{t("advisories")}</div>
                    </div>
                    <div>
                      <div className="text-sm font-black text-emerald-600">{z.reach}</div>
                      <div className="text-[8px] text-slate-400">{t("reach")}</div>
                    </div>
                  </div>
                  <span className="badge badge-live text-[8px] flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot mr-1" />{t(z.status)}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Broadcast History */}
      <ChartCard title="Broadcast Activity Log — Today"
        subtitle="All broadcasts sent with type, channels, reach and language"
        action={
          <div className="flex gap-1 flex-wrap">
            {["all","Safety","Route","Medical","Transport","Missing","Correction"].map(bt => (
              <button key={bt} onClick={() => setBroadcastType(bt)}
                className={`text-[8px] font-black px-2 py-1 rounded-lg transition-colors capitalize ${
                  broadcastType === bt ? "gov-gradient text-white" : "bg-slate-100 text-slate-600"
                }`}>{t(bt)}</button>
            ))}
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th>{t("Time")}</th><th>{t("Advisory Title")}</th><th>{t("Type")}</th>
                <th className="text-center">{t("Channels")}</th><th>{t("Language")}</th><th className="text-right">{t("Reach")}</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {filteredHistory.map((b, i) => (
                  <motion.tr key={b.time + b.title}
                    initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} transition={{ delay: i * 0.04 }}>
                    <td className="font-mono text-slate-400">{b.time}</td>
                    <td className="font-semibold text-slate-800">{t(b.title)}</td>
                    <td>
                      <span className="text-[8px] font-black px-2 py-0.5 rounded-full text-white"
                        style={{ background: BROADCAST_TYPE_COLORS[b.type] || "#64748B" }}>
                        {t(b.type)}
                      </span>
                    </td>
                    <td className="text-center font-bold text-slate-700">{b.channels}</td>
                    <td className="text-[9px] text-slate-500">{t(b.lang)}</td>
                    <td className="text-right font-black text-blue-700">{b.reach}</td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Social Media War Room + Press Briefings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Social Media War Room" subtitle="Trending topics · AI monitoring · government response status" live>
          <div className="space-y-1.5">
            {SOCIAL_MEDIA.map((s, i) => (
              <motion.div key={s.topic}
                initial={{ opacity:0, y:3 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.05 }}
                className="flex items-center gap-2 p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors flex-wrap"
              >
                <div className="flex-1 min-w-[120px]">
                  <div className="text-[10px] font-black text-slate-900">{t(s.topic)}</div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="text-[8px] text-slate-400">{t(s.platform)}</span>
                    <span className="text-[8px] text-slate-400">·</span>
                    <span className="text-[9px] font-bold text-blue-700">{s.volume}</span>
                  </div>
                </div>
                <div className="flex gap-1.5 flex-shrink-0 flex-wrap">
                  <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full ${sentBg(s.sentiment)}`}>{t(s.sentiment)}</span>
                  <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full ${govBg(s.govt)}`}>{t(s.govt)}</span>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="text-[9px] text-violet-600 font-bold mt-2 pt-2 border-t border-slate-100">
            ⚠ {t("AI social monitoring — no individual user tracking · DPDP 2023 compliant")}
          </p>
        </ChartCard>

        <ChartCard title="Press Briefing Schedule" subtitle="Media interactions · authority · venue · status">
          <div className="space-y-2">
            {PRESS_BRIEFINGS.map((p, i) => (
              <motion.div key={p.time}
                initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                className={`p-3 rounded-xl border ${
                  p.status === "Upcoming" ? "border-amber-200 bg-amber-50/20" :
                  p.status === "Completed" ? "border-emerald-200" : "border-blue-200 bg-blue-50/10"
                }`}
              >
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[160px]">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <Clock size={10} className="text-slate-400" />
                      <span className="text-[10px] font-black text-blue-700">{p.time}</span>
                      <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${pressBg(p.status)}`}>{t(p.status)}</span>
                    </div>
                    <p className="text-xs font-semibold text-slate-800 leading-snug">{t(p.title)}</p>
                    <div className="flex items-center gap-2 text-[9px] text-slate-500 mt-1 flex-wrap">
                      <span><Mic size={8} className="inline text-slate-400 mr-0.5" />{p.authority}</span>
                      <span><MapPin size={8} className="inline text-slate-400 mr-0.5" />{t(p.venue)}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Community Leader Network */}
      <ChartCard title="Community Leader Network — Briefed"
        subtitle="Key religious, civic and community leaders contacted with advisory content · Simhastha 2027">
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th>{t("Leader / Contact")}</th><th>{t("Area")}</th><th>{t("Role")}</th>
                <th className="text-center">{t("Contacted")}</th><th>{t("Status")}</th><th>{t("Message / Action")}</th>
              </tr>
            </thead>
            <tbody>
              {COMMUNITY_LEADERS.map((l, i) => (
                <motion.tr key={l.name} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.06 }}>
                  <td className="font-bold text-slate-900">{l.name}</td>
                  <td className="text-slate-600">{t(l.area)}</td>
                  <td><span className="text-[9px] text-blue-700 font-bold bg-blue-50 px-1.5 py-0.5 rounded-full">{t(l.role)}</span></td>
                  <td className="text-center font-mono text-slate-400">{l.contactTime}</td>
                  <td><span className="badge badge-success text-[8px]"><CheckCircle size={9} className="mr-0.5" />{t(l.status)}</span></td>
                  <td className="text-[10px] text-slate-600">{t(l.msg)}</td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Historical Benchmark */}
      <ChartCard title="Historical Communication Benchmark — Kumbh 2015 · 2019 · 2027"
        subtitle="Public communications evolution across three Simhastha / Kumbh cycles">
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th className="text-left">{t("Metric")}</th>
                <th className="text-center text-slate-500">{t("Nashik 2015")}</th>
                <th className="text-center text-blue-700">{t("Nashik 2019")}</th>
                <th className="text-center text-emerald-700 font-black">{t("Simhastha 2027")}</th>
                <th className="text-center">{t("Change")}</th>
              </tr>
            </thead>
            <tbody>
              {HISTORICAL.map((r, i) => (
                <motion.tr key={r.metric} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.06 }}>
                  <td className="font-black text-slate-900">{t(r.metric)}</td>
                  <td className="text-center text-slate-500">{r.k2015}</td>
                  <td className="text-center text-blue-700 font-semibold">{r.k2019}</td>
                  <td className="text-center font-black text-emerald-700">{r.k2027}</td>
                  <td className="text-center">
                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${
                      r.delta.startsWith("-") ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"
                    }`}>{t(r.delta)}</span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[9px] text-slate-500 mt-3 pt-2 border-t border-slate-100">
          {t("2027 digital expansion (WhatsApp, Sarkari App, SMS Gateway) accounts for 90% of reach increase vs 2019.")}
        </p>
      </ChartCard>
    </div>
  );
}






